import React from 'react';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';
import { Box, BoxProps, Divider, Flex, JoyKitThemeProvider, TooltipV2 } from '@withjoy/joykit';
import { Skeleton, SkeletonGroup } from '@shared/components/Skeleton';
import { Info } from '@withjoy/joykit/icons';
import useHotelPricing from '../../../../../hooks/useHotelPricing';

// Joy places display pricing differently from room blocks
export const JoyPlacePricing = ({ accommodation, showNuiteePrice, ...boxProps }: { accommodation: AccommodationCombined; showNuiteePrice: boolean } & BoxProps) => {
  const { eventInfo } = useEventInfo();
  const eventDisplayName = eventInfo?.eventDisplayName;
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const { price, loading } = useHotelPricing({ joyPlaceId: accommodation.joyPlaceId || '', showNuiteePrice });

  return loading || price ? (
    // reset typography
    <JoyKitThemeProvider>
      <Box {...boxProps}>
        <Divider marginBottom={5} />
        <SkeletonGroup
          isReady={!loading}
          placeholder={
            <Flex gap={2} justifyContent="space-between" alignItems={'flex-start'}>
              <Skeleton
                placeholder={
                  <Box width={'8rem'} typographyVariant={'body1'}>
                    &nbsp;
                  </Box>
                }
              />

              <Box>
                <Box display={'flex'} typographyVariant={'body1'} alignItems={'center'} justifyContent={'flex-end'} marginBottom={1}>
                  <Skeleton
                    placeholder={
                      <Box width={'3.5rem'} typographyVariant={'body1'}>
                        &nbsp;
                      </Box>
                    }
                  />
                </Box>
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                  <Skeleton
                    placeholder={
                      <Box width={'3.5rem'} typographyVariant={'hed2'}>
                        &nbsp;
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Flex>
          }
        >
          {price && (
            <Flex gap={2} justifyContent="space-between" alignItems={'flex-start'}>
              <Box typographyVariant={'body1'} color={'inputPlaceholderDisabled'}>
                {accTrans.roomsStartingFrom()}
              </Box>
              <Box>
                <Box display={'flex'} typographyVariant={'body1'} alignItems={'center'} justifyContent={'flex-end'} marginBottom={1}>
                  Exclusive Rate
                  {eventDisplayName ? (
                    <Box marginLeft={2}>
                      <TooltipV2 content={accTrans.exclusiveRateInfo({ name: eventDisplayName })} onMouseEnterDelay={500} placement={'topEnd'}>
                        <Info size={'sm'} color={'mono10'} title={undefined} />
                      </TooltipV2>
                    </Box>
                  ) : null}
                </Box>
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                  <Box typographyVariant={'hed2'} textAlign={'right'}>
                    {accTrans.pricePerNight({ price })}
                  </Box>
                </Box>
              </Box>
            </Flex>
          )}
        </SkeletonGroup>
      </Box>
    </JoyKitThemeProvider>
  ) : null;
};
